import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, MessageCircle, Lock, Sparkles } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { randomCode } from "../data/accessCodes";
import { generateTelegramLink } from "../data/telegramLinks";

export const ScratchCard = () => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [code] = useState(() => randomCode());
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState(0);
  const drawingRef = useRef(false);

  // Set up scratch canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Premium metallic gradient overlay
    const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, "#D7B56D");
    grad.addColorStop(0.35, "#A9824A");
    grad.addColorStop(0.6, "#E6E0D6");
    grad.addColorStop(0.85, "#D7B56D");
    grad.addColorStop(1, "#A9824A");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Add micro pattern lines
    ctx.strokeStyle = "rgba(7, 9, 13, 0.18)";
    ctx.lineWidth = 0.6;
    for (let i = 0; i < rect.width; i += 6) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + rect.height, rect.height);
      ctx.stroke();
    }

    ctx.globalCompositeOperation = "destination-out";
  }, [revealed]);

  const computeProgress = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    try {
      const data = ctx.getImageData(0, 0, width, height).data;
      let cleared = 0;
      const step = 64; // sample
      let total = 0;
      for (let i = 3; i < data.length; i += step * 4) {
        total++;
        if (data[i] < 50) cleared++;
      }
      return total ? cleared / total : 0;
    } catch {
      return 0;
    }
  };

  const draw = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();

    const p = computeProgress();
    setProgress(p);
    if (p > 0.55 && !revealed) {
      // auto reveal
      setRevealed(true);
    }
  };

  const handlePointerDown = (e) => {
    drawingRef.current = true;
    handlePointerMove(e);
  };
  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const point = e.touches?.[0] || e;
    const x = point.clientX - rect.left;
    const y = point.clientY - rect.top;
    draw(x, y);
  };
  const handlePointerUp = () => {
    drawingRef.current = false;
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  const revealNow = () => setRevealed(true);

  const sendLink = generateTelegramLink("learning-access-code", code);

  return (
    <section id="codes" className="relative py-24 md:py-32 px-5 md:px-10 lg:px-16" data-testid="access-code-section">
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-14 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#A9824A] mb-3">
            · 09 / Code Reveal
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#F5F1EA] leading-[0.98]">
            {t("codeTitle")}
          </h2>
          <p className="mt-5 text-[15px] text-[#E6E0D6]/80 leading-relaxed">{t("codeSub")}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Scratch card visual */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-3xl overflow-hidden border border-[#D7B56D]/40 glass-strong p-1">
              <div className="relative rounded-[22px] overflow-hidden bg-[#07090D] aspect-[4/3] sm:aspect-[16/10]">
                {/* Hidden code layer */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D7B56D] mb-3">
                    Access Code
                  </div>
                  <div className="font-mono text-3xl sm:text-5xl md:text-6xl font-bold text-gold-shine tracking-[0.06em]" data-testid="access-code-value">
                    {code}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A9824A] mt-4">
                    Educational Routing Only
                  </div>

                  {revealed && (
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="mt-6 flex flex-wrap gap-2 justify-center"
                    >
                      <button
                        onClick={onCopy}
                        className="btn-gold !py-2.5 !px-5 text-[12px]"
                        data-testid="code-copy-btn"
                      >
                        {copied ? <Check size={13} /> : <Copy size={13} />}
                        {copied ? t("codeCopied") : t("codeCopy")}
                      </button>
                      <a
                        href={sendLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost !py-2.5 !px-5 text-[12px]"
                        data-testid="code-send-btn"
                      >
                        <MessageCircle size={13} className="text-[#76D9E8]" />
                        {t("codeSendCode")}
                      </a>
                    </motion.div>
                  )}
                </div>

                {/* Scratch overlay canvas */}
                {!revealed && (
                  <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
                    onMouseDown={handlePointerDown}
                    onMouseMove={handlePointerMove}
                    onMouseUp={handlePointerUp}
                    onMouseLeave={handlePointerUp}
                    onTouchStart={handlePointerDown}
                    onTouchMove={handlePointerMove}
                    onTouchEnd={handlePointerUp}
                    data-testid="scratch-canvas"
                  />
                )}

                {/* Hint overlay */}
                {!revealed && progress < 0.05 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-[#0a0b0f]/60 backdrop-blur flex items-center justify-center border border-[#0a0b0f]/40">
                        <Lock size={18} className="text-[#0a0b0f]" />
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0a0b0f]">
                        {t("codeRevealHint")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!revealed && (
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1 h-1 bg-[#1A1C22] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#D7B56D] to-[#76D9E8] transition-all"
                    style={{ width: `${Math.min(100, Math.round(progress * 180))}%` }}
                  />
                </div>
                <button
                  onClick={revealNow}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D7B56D] hover:text-[#F5F1EA] transition-colors"
                  data-testid="code-reveal-btn"
                >
                  <Sparkles size={11} className="inline-block mr-1" /> {t("codeReveal")}
                </button>
              </div>
            )}
          </motion.div>

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
            data-testid="code-instructions"
          >
            <div className="rounded-3xl border border-[#D8DCE2]/8 bg-[#1A1C22] p-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#76D9E8] mb-3">
                {t("codeHowToUse")}
              </div>
              <h3 className="font-display text-xl font-bold text-[#F5F1EA] mb-5 leading-tight">
                {t("codeBody")}
              </h3>
              <ol className="space-y-3 mb-7">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <li key={n} className="flex items-start gap-3">
                    <span className="font-mono text-[10px] tracking-tight w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-[#D7B56D]/15 text-[#D7B56D]">
                      {String(n).padStart(2, "0")}
                    </span>
                    <span className="text-[13px] text-[#E6E0D6]/85 leading-relaxed">
                      {t(`codeStep${n}`)}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="pt-5 border-t border-[#D8DCE2]/8">
                <p className="text-[11px] text-[#D8DCE2]/45 leading-relaxed">{t("codeDisclaimer")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
